import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";

type Axial = { q: number; r: number };
type Camera = { x: number; y: number; zoom: number };

type PersistedStateV1 = {
  placed: string[];
  camera: Camera;
};

const STORAGE_KEY = "peslar:hdd:v1";
const DEFAULT_CAMERA: Camera = { x: 0, y: 0, zoom: 1 };

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function axialKey(a: Axial) {
  return `${a.q},${a.r}`;
}

function parseAxialKey(key: string): Axial | null {
  const parts = key.split(",");
  if (parts.length !== 2) return null;
  const q = Number(parts[0]);
  const r = Number(parts[1]);
  if (!Number.isFinite(q) || !Number.isFinite(r)) return null;
  return { q, r };
}

function hexToPixel(pointy: Axial, radius: number) {
  // pointy-top axial -> pixel (world space)
  const x = radius * Math.sqrt(3) * (pointy.q + pointy.r / 2);
  const y = radius * 1.5 * pointy.r;
  return { x, y };
}

function pixelToHex(pointy: { x: number; y: number }, radius: number) {
  // inverse of hexToPixel for pointy-top axial (fractional axial)
  const q = ((Math.sqrt(3) / 3) * pointy.x - (1 / 3) * pointy.y) / radius;
  const r = ((2 / 3) * pointy.y) / radius;
  return { q, r };
}

function axialRound(frac: { q: number; r: number }): Axial {
  // axial -> cube, round, cube -> axial
  const x = frac.q;
  const z = frac.r;
  const y = -x - z;

  let rx = Math.round(x);
  let ry = Math.round(y);
  let rz = Math.round(z);

  const xDiff = Math.abs(rx - x);
  const yDiff = Math.abs(ry - y);
  const zDiff = Math.abs(rz - z);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  return { q: rx, r: rz };
}

function hexPolygonPoints(center: { x: number; y: number }, radius: number) {
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 90);
    pts.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    });
  }
  return pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

function useResizeObserverSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      setSize({ width: cr.width, height: cr.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, size };
}

function HexSvg(props: {
  center: { x: number; y: number };
  radius: number;
  variant: "placeholder" | "solid";
  className?: string;
  dashed?: boolean;
}) {
  const { center, radius, variant, className, dashed } = props;
  const points = useMemo(() => hexPolygonPoints(center, radius), [center, radius]);

  return (
    <polygon
      className={className}
      points={points}
      fill={variant === "solid" ? "currentColor" : "transparent"}
      stroke="currentColor"
      strokeWidth={2}
      strokeDasharray={dashed ? "6 6" : undefined}
      vectorEffect="non-scaling-stroke"
    />
  );
}

function safeLoad(): PersistedStateV1 {
  if (typeof window === "undefined") return { placed: [], camera: DEFAULT_CAMERA };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { placed: [], camera: DEFAULT_CAMERA };
    const parsed = JSON.parse(raw) as Partial<PersistedStateV1>;
    const placed = Array.isArray(parsed.placed)
      ? parsed.placed.filter((k) => typeof k === "string")
      : [];
    const cam = parsed.camera ?? DEFAULT_CAMERA;
    const camera: Camera = {
      x: typeof cam.x === "number" ? cam.x : 0,
      y: typeof cam.y === "number" ? cam.y : 0,
      zoom: typeof cam.zoom === "number" ? cam.zoom : 1,
    };
    return { placed, camera };
  } catch {
    return { placed: [], camera: DEFAULT_CAMERA };
  }
}

export function HexagonDragAndDrop() {
  const { t } = useTranslation();

  const initial = useMemo(() => safeLoad(), []);
  const [placedKeys, setPlacedKeys] = useState<Set<string>>(() => new Set(initial.placed));
  const [camera, setCamera] = useState<Camera>(() => initial.camera);

  const { ref: boardRef, size: boardSize } = useResizeObserverSize<HTMLDivElement>();

  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      try {
        const payload: PersistedStateV1 = {
          placed: Array.from(placedKeys),
          camera,
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // ignore storage failures
      }
    }, 200);
    return () => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [camera, placedKeys]);

  const HEX_RADIUS = 34; // world px (before camera scale)

  const screenToWorld = useCallback(
    (s: { x: number; y: number }) => ({
      x: (s.x - camera.x) / camera.zoom,
      y: (s.y - camera.y) / camera.zoom,
    }),
    [camera],
  );

  const boardLocalFromEvent = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const el = boardRef.current;
      if (!el) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    [boardRef],
  );

  const visibleAxials = useMemo(() => {
    const w = Math.max(1, boardSize.width);
    const h = Math.max(1, boardSize.height);

    const cornersWorld = [
      screenToWorld({ x: 0, y: 0 }),
      screenToWorld({ x: w, y: 0 }),
      screenToWorld({ x: 0, y: h }),
      screenToWorld({ x: w, y: h }),
    ];

    const axialCorners = cornersWorld.map((pt) => axialRound(pixelToHex(pt, HEX_RADIUS)));

    let minQ = Infinity,
      maxQ = -Infinity,
      minR = Infinity,
      maxR = -Infinity;
    for (const a of axialCorners) {
      minQ = Math.min(minQ, a.q);
      maxQ = Math.max(maxQ, a.q);
      minR = Math.min(minR, a.r);
      maxR = Math.max(maxR, a.r);
    }

    const buffer = 5;
    minQ -= buffer;
    maxQ += buffer;
    minR -= buffer;
    maxR += buffer;

    const coords: Axial[] = [];
    for (let r = minR; r <= maxR; r++) {
      for (let q = minQ; q <= maxQ; q++) {
        coords.push({ q, r });
      }
    }
    return coords;
  }, [HEX_RADIUS, boardSize.height, boardSize.width, screenToWorld]);

  const visibleKeySet = useMemo(() => new Set(visibleAxials.map(axialKey)), [visibleAxials]);

  const visiblePlaced = useMemo(() => {
    const out: Axial[] = [];
    for (const key of placedKeys) {
      if (!visibleKeySet.has(key)) continue;
      const a = parseAxialKey(key);
      if (a) out.push(a);
    }
    return out;
  }, [placedKeys, visibleKeySet]);

  const pointerPanRef = useRef<{
    pointerId: number;
    start: { x: number; y: number };
    startCamera: Camera;
    moved: boolean;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      const el = boardRef.current;
      if (!el) return;

      // If user clicked on a cell, we’ll let the click handler toggle;
      // background pan starts if we pressed on the board surface.
      const local = boardLocalFromEvent(e);
      pointerPanRef.current = {
        pointerId: e.pointerId,
        start: local,
        startCamera: camera,
        moved: false,
      };

      el.setPointerCapture(e.pointerId);
    },
    [boardLocalFromEvent, boardRef, camera],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const pan = pointerPanRef.current;
      if (!pan) return;
      if (pan.pointerId !== e.pointerId) return;

      const local = boardLocalFromEvent(e);
      const dx = local.x - pan.start.x;
      const dy = local.y - pan.start.y;
      if (Math.hypot(dx, dy) > 3) {
        pan.moved = true;
      }

      if (!pan.moved) return;

      setCamera((c) => ({
        ...c,
        x: pan.startCamera.x + dx,
        y: pan.startCamera.y + dy,
      }));
    },
    [boardLocalFromEvent],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const pan = pointerPanRef.current;
      if (!pan) return;
      if (pan.pointerId !== e.pointerId) return;
      pointerPanRef.current = null;
    },
    [],
  );

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const local = boardLocalFromEvent(e);

      setCamera((c) => {
        const zoomDelta = Math.exp(-e.deltaY * 0.0015);
        const nextZoom = clamp(c.zoom * zoomDelta, 0.35, 2.5);

        // zoom around cursor: keep world point under cursor stable
        const worldBefore = {
          x: (local.x - c.x) / c.zoom,
          y: (local.y - c.y) / c.zoom,
        };

        const nextX = local.x - worldBefore.x * nextZoom;
        const nextY = local.y - worldBefore.y * nextZoom;

        return { x: nextX, y: nextY, zoom: nextZoom };
      });
    },
    [boardLocalFromEvent],
  );

  const hoveredCell = useRef<Axial | null>(null);

  const toggleAtScreenPoint = useCallback(
    (screen: { x: number; y: number }) => {
      const world = screenToWorld(screen);
      const a = axialRound(pixelToHex(world, HEX_RADIUS));
      const key = axialKey(a);
      setPlacedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [HEX_RADIUS, screenToWorld],
  );

  const onClickBoard = useCallback(
    (e: React.MouseEvent) => {
      // Ignore clicks after panning gesture
      const pan = pointerPanRef.current;
      if (pan?.moved) return;
      const local = boardLocalFromEvent(e);
      toggleAtScreenPoint(local);
    },
    [boardLocalFromEvent, toggleAtScreenPoint],
  );

  const reset = useCallback(() => {
    setPlacedKeys(new Set());
    setCamera(DEFAULT_CAMERA);
  }, []);

  const filledCount = placedKeys.size;

  const boardCursor = pointerPanRef.current?.moved ? "grabbing" : "grab";

  return (
    <div className="hexagon-drag-and-drop">
      <div className="hdd-header">
        <div className="hdd-titles">
          <div className="hdd-title">{t("hexagonDragAndDrop.boardTitle")}</div>
          <div className="hdd-subtitle">
            {t("hexagonDragAndDrop.filledSlots", { count: filledCount })} •{" "}
            {t("hexagonDragAndDrop.zoomLabel", { zoom: Math.round(camera.zoom * 100) })}%
          </div>
        </div>
        <div className="hdd-actions">
          <button type="button" className="hdd-btn primary" onClick={reset}>
            <RotateCcw size={16} />
            {t("hexagonDragAndDrop.reset")}
          </button>
        </div>
      </div>

      <div className="hdd-hint">{t("hexagonDragAndDrop.panZoomHint")}</div>

      <div
        ref={boardRef}
        className="hdd-board hdd-board--infinite"
        role="application"
        style={{ cursor: boardCursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onClick={onClickBoard}
      >
        <svg className="hdd-board-svg" width="100%" height="100%" aria-hidden="true">
          <g transform={`translate(${camera.x} ${camera.y}) scale(${camera.zoom})`}>
            {/* placeholders */}
            <g className="hdd-placeholders" style={{ color: "var(--border-quinary)" }}>
              {visibleAxials.map((a) => {
                const c = hexToPixel(a, HEX_RADIUS);
                const key = axialKey(a);
                const isPlaced = placedKeys.has(key);
                return (
                  <g
                    key={key}
                    className={[
                      "hdd-hex",
                      "hdd-hex--placeholder",
                      isPlaced ? "is-occupied" : "",
                    ].join(" ")}
                  >
                    <HexSvg
                      center={c}
                      radius={HEX_RADIUS}
                      variant="placeholder"
                      className="hdd-hex-placeholder"
                      dashed
                    />
                  </g>
                );
              })}
            </g>

            {/* placed */}
            <g className="hdd-placed" style={{ color: "var(--accent-secondary)" }}>
              {visiblePlaced.map((a) => {
                const c = hexToPixel(a, HEX_RADIUS);
                const key = axialKey(a);
                return (
                  <g key={`placed_${key}`} className="hdd-piece">
                    <HexSvg
                      center={c}
                      radius={HEX_RADIUS}
                      variant="solid"
                      className="hdd-hex-solid"
                    />
                  </g>
                );
              })}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}

